import {
	clientUnavailableReason,
	jobsCountLabel,
	portKindLabel,
	redundantPrinterNames,
} from "./printer-labels";
import type { PlanAssessment, PlanItem } from "./printer-plan";
import type {
	DeviceConnection,
	NetworkInfo,
	NetworkPrinterCandidate,
	PharmacyPrintersResponse,
	PrinterDevice,
	PrinterItem,
} from "./printers-api";

/**
 * Model mapy apteki: stanowiska (komputery z agentem), fizyczne drukarki i
 * linie między nimi. Jedna drukarka = jeden węzeł TYLKO wtedy, gdy wiadomo to
 * na pewno: udział zarządzany przez moduł (połączenia stanowisk do kolejki
 * komputera z drukarką) albo ten sam adres IP drukarki sieciowej TCP/IP.
 * Ręczne połączenia `\\komputer\kolejka`, kolejki WSD i kolejki bez adresu to
 * osobne węzły z adnotacją — po samej nazwie niczego nie scalamy.
 */

export type PrinterNodeKind = "cable" | "network" | "wsd" | "manual" | "other";

/** Kolejka albo połączenie na konkretnym komputerze, które prowadzi do węzła drukarki. */
export type Installation = {
	device: PrinterDevice;
	/** Kolejka Windows (kabel, TCP/IP, WSD, inna). */
	printer: PrinterItem | null;
	/** Połączenie sieciowe (zarządzane albo ręczne). */
	connection: DeviceConnection | null;
	isDefault: boolean;
	/** Linia: kabel komputera z drukarką albo połączenie/kolejka sieciowa. */
	link: "cable" | "conn";
};

export type PrinterNode = {
	id: string;
	kind: PrinterNodeKind;
	name: string;
	/** Inne nazwy tej samej drukarki (kolejki pod adresem IP nazwane różnie). */
	altNames: string[];
	/** Kabel/USB/TCP/IP/WSD… — krótki opis rodzaju. */
	kindLabel: string;
	/** Komputer z drukarką na kablu. */
	host: PrinterDevice | null;
	/** Adres drukarki sieciowej TCP/IP. */
	address: string | null;
	installations: Installation[];
	/** Problemy widoczne na węźle: stojące zadania, pauza, błąd, offline. */
	problems: string[];
	/** Adnotacje zamiast zgadywania (ręczne połączenie, możliwy duplikat). */
	notes: string[];
	/** Wykryta w sieci, nigdzie niezainstalowana. */
	discovered: NetworkPrinterCandidate | null;
	/** Dlaczego tej drukarki nie da się przypisać innemu stanowisku; `null` = da się. */
	assignBlock: string | null;
};

export type StationNode = {
	id: string;
	device: PrinterDevice;
	network: NetworkInfo | null;
	/** Konta Windows z inwentarza (profile zalogowanych z połączeniami drukarek). */
	users: string[];
	/** Dlaczego temu stanowisku nie da się nic przypisać; `null` = da się. */
	assignBlock: string | null;
	/** Kolejki pominięte na mapie (wirtualne, z pulpitu zdalnego) — są w szczegółach komputera. */
	hiddenCount: number;
};

export type MapEdge = {
	id: string;
	from: string;
	to: string;
	kind: "cable" | "conn" | "plan";
	isDefault: boolean;
	/** Pozycja planu, z której wynika linia planowana. */
	planItemId?: string;
	/** Pozycja właśnie się wykonuje — tylko wtedy linia planowana jest animowana. */
	running?: boolean;
};

export type NetworkSummary = {
	/** Najczęstsza sieć stanowisk (sieć apteki). */
	main: NetworkInfo | null;
	/** Ile komputerów zgłosiło sieć. */
	known: number;
	/** Komputery w innej sieci niż główna. */
	outside: number;
};

export type PharmacyMap = {
	network: NetworkSummary;
	stations: StationNode[];
	printers: PrinterNode[];
	edges: MapEdge[];
};

const CABLE_KINDS = new Set(["usb", "dot4", "lpt", "com"]);

/**
 * Na mapie pomijamy wirtualne (PDF, XPS, faks) i drukarki z pulpitu zdalnego.
 * Agent przed 0.1.128 nie rozpoznawał portu CutePDF (`CPW4:`) — kolejkę
 * z innym portem i sterownikiem PDF/XPS też traktujemy jak wirtualną.
 */
export function mapHidden(printer: PrinterItem): boolean {
	if (printer.portKind === "virtual" || printer.portKind === "redirected") return true;
	return printer.portKind === "other" && /pdf|xps/i.test(printer.driver ?? "");
}

function normalizedName(name: string): string {
	return name
		.replace(/\s*\((kopia|copy) \d+\)$/i, "")
		.trim()
		.toLocaleLowerCase("pl");
}

function hhmm(iso: string): string | null {
	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) return null;
	return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

/** Problemy kolejki do pokazania na węźle (krótko, bez zawijania). */
export function printerProblems(printer: PrinterItem, where?: string): string[] {
	const at = where ? ` · ${where}` : "";
	const problems: string[] = [];
	if (printer.jobs?.error) problems.push(`błąd wydruku${at}`);
	if (printer.jobs && printer.jobs.count > 0) {
		const since = printer.jobs.oldestAt ? hhmm(printer.jobs.oldestAt) : null;
		problems.push(
			`${jobsCountLabel(printer.jobs.count)} ${printer.jobs.count === 1 ? "stoi" : "stoją"}${since ? ` od ${since}` : ""}${at}`,
		);
	}
	if (printer.paused) problems.push(`wstrzymana${at}`);
	if (printer.offline) problems.push(`wyłączona${at}`);
	return problems;
}

/** Sieć apteki: najczęstsza podsieć wśród komputerów, które ją zgłosiły. */
export function summarizeNetwork(
	devices: readonly PrinterDevice[],
	networks: ReadonlyMap<string, NetworkInfo | null>,
): NetworkSummary {
	const counts = new Map<string, { info: NetworkInfo; count: number }>();
	let known = 0;
	for (const device of devices) {
		const info = networks.get(device.deviceUuid);
		if (!info) continue;
		known += 1;
		const entry = counts.get(info.subnet) ?? { info, count: 0 };
		entry.count += 1;
		counts.set(info.subnet, entry);
	}
	const main = [...counts.values()].sort((a, b) => b.count - a.count)[0]?.info ?? null;
	const outside = main
		? devices.filter((d) => {
				const info = networks.get(d.deviceUuid);
				return info != null && info.subnet !== main.subnet;
			}).length
		: 0;
	return { main, known, outside };
}

/**
 * Dlaczego stanowisku nie da się przypisać żadnej drukarki (wyszarzenie na
 * mapie). Kolejność jak przyczyny backendu: miejsce, offline, agent, sieć.
 */
export function stationAssignBlock(
	device: PrinterDevice,
	view: PharmacyPrintersResponse,
	sharing: boolean,
	network: NetworkInfo | null,
	main: NetworkInfo | null,
): string | null {
	if (!sharing) return "Grupa urządzeń bez apteki — to nie sieć jednej apteki, bez udostępniania.";
	const reason = clientUnavailableReason(device, view.minAgentVersion, false);
	if (reason === "offline") return "Komputer offline.";
	if (reason) return `${reason.charAt(0).toUpperCase()}${reason.slice(1)}.`;
	if (network && main && network.subnet !== main.subnet) {
		return `Inna sieć (${network.subnet}) niż sieć apteki.`;
	}
	return null;
}

/**
 * Czy tę drukarkę da się przypisać temu stanowisku; `null` = da się. Kablowa:
 * udostępnienie z komputera z drukarką; sieciowa TCP/IP: dodanie kolejki na
 * stanowisku w tej samej sieci. Reszta (WSD, ręczne połączenia, kolejki bez
 * adresu) — nie wiadomo pewnie, co to za drukarka, więc nie przypisujemy.
 */
export function assignBlocker(
	node: PrinterNode,
	station: StationNode,
	networks: ReadonlyMap<string, NetworkInfo | null>,
	minAgentVersion: string,
): string | null {
	if (node.assignBlock) return node.assignBlock;
	if (node.installations.some((i) => i.device.deviceUuid === station.id)) {
		return `${station.device.displayName} już ma tę drukarkę.`;
	}
	if (node.kind === "cable") {
		if (station.assignBlock) return station.assignBlock;
		const host = node.host;
		if (!host) return "Nie wiadomo, do którego komputera drukarka jest podłączona.";
		const hostNet = networks.get(host.deviceUuid);
		const stationNet = networks.get(station.id);
		if (hostNet && stationNet && hostNet.subnet !== stationNet.subnet) {
			return `${station.device.displayName} jest w innej sieci niż ${host.displayName}.`;
		}
		const reason = clientUnavailableReason(station.device, minAgentVersion, false);
		return reason ? `${station.device.displayName}: ${reason}.` : null;
	}
	if (node.kind === "network") {
		if (!station.device.online) return "Komputer offline.";
		if (!station.device.addSupported) {
			return "Komputer nie zgłosił sieci albo ma za starego agenta — dodawanie drukarek niedostępne.";
		}
		const net = networks.get(station.id);
		if (net && node.address && !ipInSubnet(node.address, net)) {
			return `Drukarka ${node.address} jest poza siecią komputera (${net.subnet}).`;
		}
		return null;
	}
	return "Nie wiadomo na pewno, co to za drukarka — przypisz ją z jej komputera albo dodaj jako sieciową.";
}

function ipToInt(ip: string): number | null {
	const parts = ip.split(".").map((p) => Number.parseInt(p, 10));
	if (parts.length !== 4 || parts.some((p) => Number.isNaN(p) || p < 0 || p > 255)) return null;
	return ((parts[0] << 24) | (parts[1] << 16) | (parts[2] << 8) | parts[3]) >>> 0;
}

export function ipInSubnet(ip: string, network: NetworkInfo): boolean {
	const a = ipToInt(ip);
	const b = ipToInt(network.ip);
	if (a == null || b == null) return true; // nie wiemy — decyduje backend
	const mask = network.prefix === 0 ? 0 : (0xffffffff << (32 - network.prefix)) >>> 0;
	return (a & mask) === (b & mask);
}

function kindLabelOf(printer: PrinterItem): string {
	if (printer.address) return `Sieciowa · ${printer.address.host}`;
	if (printer.portKind === "other" && printer.port) {
		// Nazwa portu (i monitor, agent ≥ 0.1.128) — widać, dokąd prowadzi kolejka.
		const monitor = printer.portMonitor ? ` (${printer.portMonitor})` : "";
		return `${portKindLabel(printer.portKind)} · ${printer.port}${monitor}`;
	}
	return portKindLabel(printer.portKind);
}

/**
 * Model mapy z widoku miejsca, sieci komputerów (`network-printers`, gdy
 * znane), wykrytych drukarek i planu (linie planowane).
 */
export function buildPharmacyMap({
	view,
	sharing,
	networks,
	discovered = [],
	plan = [],
	assessments,
}: {
	view: PharmacyPrintersResponse;
	sharing: boolean;
	networks: ReadonlyMap<string, NetworkInfo | null>;
	/** Kandydaci z `network-printers` (dowolnego komputera sieci apteki); `foundBy` — komputer, który ich zgłosił. */
	discovered?: readonly (NetworkPrinterCandidate & { foundBy?: string })[];
	plan?: readonly PlanItem[];
	assessments?: ReadonlyMap<string, PlanAssessment>;
}): PharmacyMap {
	const network = summarizeNetwork(view.devices, networks);
	// Ten sam adres IP to ta sama drukarka tylko w jednej sieci: w aptece (jedna sieć
	// lokalna) wprost, w grupie bez apteki tylko w tej samej znanej sieci komputera.
	const ipNodeId = (host: string, deviceUuid: string | undefined): string => {
		if (sharing) return `ip:${host}`;
		const info = deviceUuid ? networks.get(deviceUuid) : null;
		const scope = info ? `${info.subnet}/${info.gatewayMac ?? info.gateway}` : (deviceUuid ?? "?");
		return `ip:${scope}:${host}`;
	};
	const stations: StationNode[] = view.devices.map((device) => {
		const info = networks.get(device.deviceUuid) ?? null;
		const users = [
			...new Set(device.connections.map((c) => c.userName).filter((u): u is string => u != null)),
		];
		return {
			id: device.deviceUuid,
			device,
			network: info,
			users,
			assignBlock: stationAssignBlock(device, view, sharing, info, network.main),
			hiddenCount: device.printers.filter((p) => p.port !== "" && mapHidden(p)).length,
		};
	});

	const nodes = new Map<string, PrinterNode>();
	const edges: MapEdge[] = [];
	const addNode = (
		node: Omit<PrinterNode, "problems" | "notes" | "altNames" | "installations">,
	) => {
		const existing = nodes.get(node.id);
		if (existing) return existing;
		const created: PrinterNode = {
			...node,
			problems: [],
			notes: [],
			altNames: [],
			installations: [],
		};
		nodes.set(node.id, created);
		return created;
	};
	const link = (node: PrinterNode, installation: Installation) => {
		node.installations.push(installation);
		edges.push({
			id: `${installation.device.deviceUuid}->${node.id}:${node.installations.length}`,
			from: installation.device.deviceUuid,
			to: node.id,
			kind: installation.link,
			isDefault: installation.isDefault,
		});
	};
	// Połączenia zarządzane prowadzą do węzła kolejki komputera z drukarką.
	const managedTargets = new Map<number, PrinterNode>();

	for (const device of view.devices) {
		const redundant = redundantPrinterNames(device.printers);
		for (const printer of device.printers) {
			if (mapHidden(printer)) continue;
			// Wiersz udziału zarządzanego bez kolejki w inwentarzu — zostaje, żeby dało się cofnąć udział.
			const ghost = printer.port === "";
			if (ghost && !printer.managedShare) continue;
			const cable =
				CABLE_KINDS.has(printer.portKind) || printer.cable || printer.managedShare != null;
			let node: PrinterNode;
			if (cable) {
				node = addNode({
					id: `cable:${device.deviceUuid}:${printer.name}`,
					kind: "cable",
					name: printer.name,
					kindLabel: ghost
						? "nie ma jej już na komputerze"
						: `Kabel ${portKindLabel(printer.portKind)}`,
					host: device,
					address: null,
					discovered: null,
					assignBlock: !sharing
						? "W grupie bez apteki nie ma udostępniania."
						: ghost
							? "Drukarki nie ma już na komputerze."
							: !printer.cable
								? "Drukarka nie jest już podłączona kablem."
								: !device.online
									? `Komputer z drukarką (${device.displayName}) jest offline.`
									: !device.supported
										? `${device.displayName} ma za starego agenta.`
										: null,
				});
				link(node, {
					device,
					printer,
					connection: null,
					isDefault: printer.isDefault,
					link: "cable",
				});
				for (const user of printer.usedBy) {
					if (!user.managed || user.connectionId == null) continue;
					managedTargets.set(user.connectionId, node);
				}
			} else if (
				(printer.portKind === "network" && printer.address) ||
				(printer.portKind === "other" && printer.portHost)
			) {
				// TCP/IP albo port innego monitora z adresem celu (np. serwer wydruku
				// w routerze za portem `ZYXEL`) — ten sam adres to ta sama drukarka.
				const host = printer.address?.host ?? printer.portHost ?? "";
				node = addNode({
					id: ipNodeId(host, device.deviceUuid),
					kind: "network",
					name: printer.name,
					kindLabel: `Sieciowa · ${host}`,
					host: null,
					address: host,
					discovered: null,
					assignBlock: null,
				});
				if (!printer.address && !node.notes.some((n) => n.startsWith("Port "))) {
					node.notes.push(
						`Port „${printer.port}”${printer.portMonitor ? ` (${printer.portMonitor})` : ""} prowadzi do ${host} — np. serwer wydruku w routerze. Na innym stanowisku dodasz ją jako drukarkę sieciową pod tym adresem.`,
					);
				}
				if (node.name !== printer.name && !node.altNames.includes(printer.name)) {
					node.altNames.push(printer.name);
				}
				link(node, {
					device,
					printer,
					connection: null,
					isDefault: printer.isDefault,
					link: "conn",
				});
			} else {
				node = addNode({
					id: `q:${device.deviceUuid}:${printer.name}`,
					kind: printer.portKind === "wsd" ? "wsd" : "other",
					name: printer.name,
					kindLabel: kindLabelOf(printer),
					host: null,
					address: null,
					discovered: null,
					assignBlock:
						printer.portKind === "wsd"
							? "Kolejka WSD — nie wiadomo na pewno, która to drukarka; dodaj ją jako sieciową TCP/IP."
							: "Nie wiadomo na pewno, co to za drukarka.",
				});
				link(node, {
					device,
					printer,
					connection: null,
					isDefault: printer.isDefault,
					link: "conn",
				});
			}
			const where =
				node.installations.length > 1 || node.kind === "network" ? device.displayName : undefined;
			node.problems.push(...printerProblems(printer, where));
			if (redundant.has(printer.name)) {
				node.notes.push(`Prawdopodobnie zbędna kopia na ${device.displayName}.`);
			}
		}
	}

	for (const device of view.devices) {
		for (const connection of device.connections) {
			const target =
				connection.managed && connection.connectionId != null
					? managedTargets.get(connection.connectionId)
					: undefined;
			if (target) {
				link(target, {
					device,
					printer: null,
					connection,
					isDefault: connection.isDefault,
					link: "conn",
				});
				if (connection.addressStale) {
					target.problems.push(`${device.displayName}: adres komputera z drukarką się zmienił`);
				}
				continue;
			}
			const name = connection.printerName ?? connection.path;
			const node = addNode({
				id: `conn:${device.deviceUuid}:${connection.path}:${connection.userName ?? ""}`,
				kind: "manual",
				name,
				kindLabel: connection.managed ? "Połączenie Farmastera" : "Połączenie ręczne",
				host: null,
				address: null,
				discovered: null,
				assignBlock:
					"Połączenie z udziałem innego komputera — przypisz drukarkę z jej komputera z drukarką.",
			});
			node.notes.push(
				connection.managed
					? "Komputer z drukarką nie zgłasza już tej kolejki."
					: connection.hostDisplayName
						? `${connection.path} — prowadzi do ${connection.hostDisplayName}, ale bez udziału Farmastera nie łączymy tego pewnie z kolejką tamtego komputera.`
						: `${connection.path} — komputer docelowy spoza agenta albo nieznany.`,
			);
			if (connection.addressStale) node.problems.push("adres komputera z drukarką się zmienił");
			link(node, {
				device,
				printer: null,
				connection,
				isDefault: connection.isDefault,
				link: "conn",
			});
		}
	}

	// Wykryte w sieci, nigdzie niezainstalowane — tylko adresy, których nie ma jeszcze na mapie.
	for (const candidate of discovered) {
		const id = ipNodeId(candidate.host, candidate.foundBy);
		if (nodes.has(id)) continue;
		addNode({
			id,
			kind: "network",
			name: candidate.suggestedName,
			kindLabel: `Sieciowa · ${candidate.host} · wykryta, nigdzie niezainstalowana`,
			host: null,
			address: candidate.host,
			discovered: candidate,
			assignBlock: null,
		});
	}

	// Ta sama nazwa w różnych węzłach (np. WSD obok TCP/IP, ręczne połączenie obok kolejki
	// na kablu) — adnotacja, nie scalenie. Same kablowe o tej samej nazwie to zwykle ten sam
	// model na dwóch komputerach, więc ich nie oznaczamy.
	const byName = new Map<string, PrinterNode[]>();
	for (const node of nodes.values()) {
		const key = normalizedName(node.name);
		byName.set(key, [...(byName.get(key) ?? []), node]);
	}
	for (const group of byName.values()) {
		if (group.length < 2 || group.every((n) => n.kind === "cable")) continue;
		for (const node of group) {
			const others = group.filter((n) => n !== node).map((n) => `${n.kindLabel}`);
			node.notes.push(
				`Ta sama nazwa co ${others.length === 1 ? "drukarka" : "drukarki"}: ${others.join("; ")} — może to jedna drukarka zainstalowana kilka razy.`,
			);
		}
	}

	// Linie planowane z pozycji planu (bez niewykonalnych i już wykonanych).
	for (const item of plan) {
		if (item.status === "succeeded" || item.status === "failed") continue;
		if (assessments?.get(item.id)?.state === "blocked") continue;
		const { action } = item;
		if (action.kind === "share") {
			const to = `cable:${action.input.hostDeviceUuid}:${action.input.printerName}`;
			if (nodes.has(to)) {
				edges.push({
					id: `plan:${item.id}`,
					from: action.input.clientDeviceUuid,
					to,
					kind: "plan",
					isDefault: action.input.setDefault,
					planItemId: item.id,
					running: item.status === "running",
				});
			}
		} else if (action.kind === "add_printer") {
			const to = ipNodeId(action.input.host, action.input.deviceUuid);
			if (!nodes.has(to)) {
				addNode({
					id: to,
					kind: "network",
					name: action.input.printerName,
					kindLabel: `Sieciowa · ${action.input.host}`,
					host: null,
					address: action.input.host,
					discovered: null,
					assignBlock: null,
				});
			}
			edges.push({
				id: `plan:${item.id}`,
				from: action.input.deviceUuid,
				to,
				kind: "plan",
				isDefault: action.input.setDefault,
				planItemId: item.id,
				running: item.status === "running",
			});
		}
	}

	const printers = orderPrinters([...nodes.values()], stations, edges);
	return { network, stations, printers, edges };
}

const KIND_ORDER: Record<PrinterNodeKind, number> = {
	cable: 0,
	network: 1,
	wsd: 2,
	other: 3,
	manual: 4,
};

/** Domyślna drukarka na górze listy; reszta w dotychczasowej kolejności (sort stabilny). */
export function defaultFirst<T extends { isDefault: boolean }>(items: readonly T[]): T[] {
	return [...items].sort((a, b) => Number(b.isDefault) - Number(a.isDefault));
}

/**
 * Kolejność drukarek w rzędzie pod stanowiskami: każda drukarka staje pod
 * średnią pozycją stanowisk, z którymi ma linię (barycentrum) — linie biegną
 * wtedy prawie pionowo i nie krzyżują się bez potrzeby. Liczą się linie
 * istniejące; planowane tylko dla drukarki bez innych linii, żeby dopisanie
 * szkicu nie przestawiało mapy. Drukarki bez linii (wykryte w sieci) na końcu.
 */
export function orderPrinters(
	printers: readonly PrinterNode[],
	stations: readonly StationNode[],
	edges: readonly MapEdge[],
): PrinterNode[] {
	const column = new Map(stations.map((s, i) => [s.id, i]));
	const positions = new Map<string, { real: number[]; plan: number[] }>();
	for (const edge of edges) {
		const index = column.get(edge.from);
		if (index == null) continue;
		const entry = positions.get(edge.to) ?? { real: [], plan: [] };
		(edge.kind === "plan" ? entry.plan : entry.real).push(index);
		positions.set(edge.to, entry);
	}
	const barycenter = (node: PrinterNode): number => {
		const entry = positions.get(node.id);
		const list = entry?.real.length ? entry.real : (entry?.plan ?? []);
		if (list.length === 0) return Number.POSITIVE_INFINITY;
		return list.reduce((sum, i) => sum + i, 0) / list.length;
	};
	const keyed = printers.map((node) => ({ node, at: barycenter(node) }));
	keyed.sort((a, b) => {
		if (a.at !== b.at) return a.at < b.at ? -1 : 1;
		return (
			KIND_ORDER[a.node.kind] - KIND_ORDER[b.node.kind] ||
			a.node.name.localeCompare(b.node.name, "pl")
		);
	});
	return keyed.map((k) => k.node);
}

/** Węzły drukarek, na które planowane są zmiany (usunięcie, naprawa, domyślna) — znacznik na węźle. */
export function plannedOnNode(node: PrinterNode, plan: readonly PlanItem[]): PlanItem[] {
	return plan.filter((item) => {
		if (item.status === "succeeded") return false;
		const { action } = item;
		const queueMatch = (deviceUuid: string, name: string) =>
			node.installations.some(
				(i) =>
					i.device.deviceUuid === deviceUuid &&
					(i.printer?.name === name || i.connection?.path === name),
			);
		switch (action.kind) {
			case "remove_printer":
			case "fix_printer":
			case "reinstall_printer":
			case "set_default":
				return queueMatch(action.input.deviceUuid, action.input.printerName);
			case "remove_connection":
				return queueMatch(action.input.deviceUuid, action.input.path);
			case "disconnect":
			case "repair":
				return node.installations.some((i) => i.connection?.connectionId === action.connectionId);
			case "unshare":
				return node.installations.some((i) => i.printer?.managedShare?.shareId === action.shareId);
			default:
				return false;
		}
	});
}
